import React, { useCallback, useEffect, useMemo, useState } from "react";
import ControlsHeader from "shared/ControlsHeader";
import SearchBar from "shared/SearchBar";
import Loader from "shared/Loader";
import {
  fetchDreamcastPlaylists,
  fetchDreamcastPlaylistById,
  createDreamcastPlaylist,
  updateDreamcastPlaylist,
  duplicateDreamcastPlaylist,
  setDreamcastPlaylistStatus,
  fetchDreamcastPlaylistTypes,
  createDreamcastPlaylistType,
  deleteDreamcastPlaylistType,
  moveDreamcastPlaylistTempImage,
} from "DreamCast/api/playlists";
import {
  fetchDreamcastMedia,
  fetchDreamcastContentSettingsConfig,
  uploadDreamcastThumbnailFile,
} from "DreamCast/api/media";
import Button from "shared/Button";
import FormActionBar from "shared/@forms/FormActionBar";
import PublishCard from "shared/@forms/PublishCard";
import PlaylistInfoForm from "shared/@forms/PlaylistInfoForm";
import SortModal from "DreamCast/Shared/SortModal";
import AudioListItem from "DreamCast/Shared/AudioListItem";
import PlaylistThumbnailPreview from "DreamCast/Shared/PlaylistThumbnailPreview";
import PlaylistListItem from "DreamCast/Shared/PlaylistListItem";
import PlaylistAudiosSection from "DreamCast/Shared/PlaylistAudiosSection";
import ContentSettingsCard from "DreamCast/Shared/ContentSettingsCard";
import {
  buildTagOptionsByTypeCode,
  extractSettingsTagDefinitions,
} from "DreamCast/Shared/ContentSettingsCard/tagSchema";
import { FilterColumn } from "shared/FilterColumn";
import { matchesCategoryFilter } from "DreamCast/Shared/categoryFilter";
import styles from "./Playlists.module.css";

const LOCATION_OPTIONS = ["WWG Playlists"];

const PIN_BITMASK = { ibo: 2, platinum: 4, emerald: 8, diamond: 16 };

const shortPermissionLabel = (id, label) => {
  const fallbackById = {
    premier: "P",
    complimentary: "C",
    ibo: "IBO",
    platinum: "PLA",
    emerald: "EME",
    diamond: "DIA",
  };

  if (fallbackById[id]) {
    return fallbackById[id];
  }

  return String(label || "")
    .trim()
    .split(/\s+/)
    .map((part) => part[0] || "")
    .join("")
    .slice(0, 3)
    .toUpperCase();
};

const resolvePermissionId = ({ kind, value, label }) => {
  const normalizedKind = String(kind || "").toUpperCase();
  const normalizedLabel = String(label || "").toUpperCase();
  const numericValue = Number(value);

  if (normalizedKind === "MEMBERSHIP") {
    if (normalizedLabel.includes("PREMIER") || numericValue === 1) {
      return "premier";
    }
    if (normalizedLabel.includes("COMPLIMENT") || numericValue === 2) {
      return "complimentary";
    }
    return null;
  }

  if (normalizedKind === "PIN") {
    if (
      normalizedLabel.includes("ASSOCIATE") ||
      normalizedLabel.includes("IBO") ||
      numericValue === 3
    ) {
      return "ibo";
    }
    if (normalizedLabel.includes("PLATINUM") || numericValue === 4) {
      return "platinum";
    }
    if (normalizedLabel.includes("EMERALD") || numericValue === 5) {
      return "emerald";
    }
    if (normalizedLabel.includes("DIAMOND") || numericValue === 6) {
      return "diamond";
    }
    return null;
  }

  return null;
};

const buildPermissionUiFromSections = (visibilitySections = []) => {
  const sections = Array.isArray(visibilitySections) ? visibilitySections : [];
  const membershipSection = sections.find((section) => {
    const code = String(section?.typeCode || "").toUpperCase();
    return code === "PERMISSIONS_MEMBERSHIP";
  });
  const pinSection = sections.find((section) => {
    const code = String(section?.typeCode || "").toUpperCase();
    return code === "PERMISSIONS_PIN_LEVEL";
  });

  const membershipGroupValuesById = { premier: 1, complimentary: 2 };

  const membershipOptions = Array.isArray(membershipSection?.options)
    ? membershipSection.options
        .map((option) => {
          const id = resolvePermissionId({
            kind: "MEMBERSHIP",
            value: option?.value,
            label: option?.label,
          });
          if (!id) {
            return null;
          }

          const value = Number(option?.value);
          if (Number.isFinite(value)) {
            membershipGroupValuesById[id] = value;
          }

          const fullLabel = String(option?.label || "").trim();
          return {
            id,
            label: shortPermissionLabel(id, fullLabel),
            fullLabel,
            icon: String(option?.icon || "").trim(),
          };
        })
        .filter(Boolean)
        // Keep reverse-alphabetical order to match current design (C before P).
        // If product wants DB order later, remove this sort and keep source order.
        .sort((a, b) =>
          String(b?.fullLabel || b?.label || "").localeCompare(
            String(a?.fullLabel || a?.label || ""),
            undefined,
            { sensitivity: "base" },
          ),
        )
    : [];

  const pinLevels = Array.isArray(pinSection?.options)
    ? pinSection.options
        .map((option) => {
          const id = resolvePermissionId({
            kind: "PIN",
            value: option?.value,
            label: option?.label,
          });
          if (!id) {
            return null;
          }

          const fullLabel = String(option?.label || "").trim();
          return {
            id,
            label: shortPermissionLabel(id, fullLabel),
            fullLabel,
            icon: String(option?.icon || "").trim(),
          };
        })
        .filter(Boolean)
    : [];

  return {
    membershipOptions,
    pinLevels,
    membershipGroupValuesById,
  };
};

const buildAdditionalVisibilitySections = (visibilitySections = []) => {
  const ignoredTypeCodes = new Set([
    "PERMISSIONS_MEMBERSHIP",
    "PERMISSIONS_PIN_LEVEL",
  ]);

  return (Array.isArray(visibilitySections) ? visibilitySections : [])
    .map((section) => {
      const typeCode = String(section?.typeCode || "").toUpperCase();
      if (
        !typeCode ||
        ignoredTypeCodes.has(typeCode) ||
        typeCode.includes("REUNION")
      ) {
        return null;
      }

      const selectorComponent = String(
        section?.selectorComponent || "CHECKBOX_LIST",
      ).toUpperCase();
      if (selectorComponent !== "CHECKBOX_LIST") {
        return null;
      }

      const title = String(section?.label || section?.type || typeCode).trim();
      const options = Array.isArray(section?.options)
        ? section.options
            .map((option) => String(option?.label || "").trim())
            .filter(Boolean)
        : [];

      return {
        typeCode,
        title,
        options,
      };
    })
    .filter(Boolean);
};

const findVisibilitySection = (sections = [], kind = "") => {
  const normalizedKind = String(kind || "").toUpperCase();

  if (!Array.isArray(sections) || !normalizedKind) {
    return null;
  }

  if (normalizedKind === "REUNION") {
    return (
      sections.find((section) => {
        const code = String(section?.typeCode || "").toUpperCase();
        return code.includes("REUNION");
      }) || null
    );
  }

  if (normalizedKind === "DIAMOND") {
    const byTypeCode = sections.find((section) => {
      const code = String(section?.typeCode || "").toUpperCase();
      return code === "DIAMONDSHIPS" || code === "DIAMONDS";
    });

    if (byTypeCode) {
      return byTypeCode;
    }

    return (
      sections.find((section) => {
        const code = String(section?.typeCode || "").toUpperCase();
        return code.includes("DIAMOND") && !code.includes("REUNION");
      }) ||
      sections.find((section) => {
        const label = String(section?.label || "").toUpperCase();
        return label.includes("DIAMONDSHIP") || label.includes("DIAMONDS");
      }) ||
      null
    );
  }

  return null;
};

const toCsvString = (value) => {
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return value.join(",");
  return "";
};

const getPinLevelPermissions = (pinPermissions = {}) =>
  Object.entries(PIN_BITMASK).reduce(
    (sum, [key, bit]) => sum + (pinPermissions[key] ? bit : 0),
    0,
  );

const ensureDefaultLanguageSelection = (tags = {}) => {
  const selected = Array.isArray(tags.selectedLanguages)
    ? tags.selectedLanguages.filter((value) => typeof value === "string")
    : Array.isArray(tags.languages)
    ? tags.languages.filter((value) => typeof value === "string")
    : [];

  return selected.length > 0 ? selected : ["English"];
};

const toApiContentSettings = (state = {}) => {
  const tags = state.tags || {};
  const membershipGroupValuesById = {
    premier: Number(state?.membershipGroupValuesById?.premier) || 1,
    complimentary: Number(state?.membershipGroupValuesById?.complimentary) || 2,
  };
  return {
    pinLevelPermissions: getPinLevelPermissions(state.pinPermissions),
    groups:
      typeof state.groups === "string"
        ? state.groups
        : [
            state.membershipPermissions?.premier
              ? String(membershipGroupValuesById.premier)
              : null,
            state.membershipPermissions?.complimentary
              ? String(membershipGroupValuesById.complimentary)
              : null,
          ]
            .filter(Boolean)
            .join(","),
    familyReunions: toCsvString(state.selectedFamilyGroups),
    diamonds: toCsvString(state.selectedDiamonds),
    additionalIbos: state.additionalIbos || "",
    visibilitySelectionsByTypeCode: state.visibilitySelectionsByTypeCode || {},
    tags: {
      selectedLocations: Array.isArray(tags.selectedLocations)
        ? tags.selectedLocations
        : Array.isArray(tags.locations)
        ? tags.locations
        : [],
      selectedLanguages: ensureDefaultLanguageSelection(tags),
      selectedOccupations: Array.isArray(tags.selectedOccupations)
        ? tags.selectedOccupations
        : Array.isArray(tags.occupations)
        ? tags.occupations
        : [],
      keywords: Array.isArray(tags.keywords) ? tags.keywords : [],
      hiddenTags: Array.isArray(tags.hiddenTags) ? tags.hiddenTags : [],
      byTypeCode:
        tags.byTypeCode && typeof tags.byTypeCode === "object"
          ? tags.byTypeCode
          : {},
    },
  };
};

const normalizeContentSettingsFromApi = (apiSettings, config) => {
  if (!apiSettings) return null;

  const bitmask = apiSettings.pinLevelPermissions ?? 0;
  const groupIds = String(apiSettings.groups || "")
    .split(",")
    .filter(Boolean)
    .map(Number);

  const rawGroups = config?.groups || [];
  const visibilitySections = Array.isArray(config?.visibilityGroupSections)
    ? config.visibilityGroupSections
    : Array.isArray(config?.visibilityGroups)
    ? config.visibilityGroups
    : [];
  const reunionSection = findVisibilitySection(visibilitySections, "REUNION");
  const diamondSection = findVisibilitySection(visibilitySections, "DIAMOND");
  const permissionUi = buildPermissionUiFromSections(visibilitySections);
  const additionalVisibilitySections =
    buildAdditionalVisibilitySections(visibilitySections);
  const visibilitySelectionsByTypeCode =
    apiSettings?.visibilitySelectionsByTypeCode &&
    typeof apiSettings.visibilitySelectionsByTypeCode === "object"
      ? apiSettings.visibilitySelectionsByTypeCode
      : {};

  const processed = config?._processedLists;
  const defaultTags = processed?.tags || DEFAULT_PERMISSION_STATE.tags;
  const optionGroups =
    processed?.groups ??
    rawGroups.map((g) => (typeof g === "string" ? g : g.name));
  const optionFamilyReunions =
    processed?.familyReunionGroups ??
    (Array.isArray(reunionSection?.options)
      ? reunionSection.options.map((option) => option.label)
      : (config?.familyReunionGroups || []).map((g) =>
          typeof g === "string" ? g : g.name,
        ));
  const optionDiamonds =
    processed?.diamonds ??
    (Array.isArray(diamondSection?.options)
      ? diamondSection.options.map((option) => option.label)
      : (config?.diamonds || []).map((d) =>
          typeof d === "string" ? d : d.name,
        ));

  // familyReunions and diamonds are stored as CSV of display names (not IDs)
  const selectedFamilyGroups = String(apiSettings.familyReunions || "")
    .split(",")
    .filter(Boolean);
  const selectedDiamonds = String(apiSettings.diamonds || "")
    .split(",")
    .filter(Boolean);

  return {
    membershipPermissions: {
      complimentary: groupIds.includes(
        Number(permissionUi.membershipGroupValuesById?.complimentary) || 2,
      ),
      premier: groupIds.includes(
        Number(permissionUi.membershipGroupValuesById?.premier) || 1,
      ),
    },
    pinPermissions: {
      ibo: !!(bitmask & PIN_BITMASK.ibo),
      platinum: !!(bitmask & PIN_BITMASK.platinum),
      emerald: !!(bitmask & PIN_BITMASK.emerald),
      diamond: !!(bitmask & PIN_BITMASK.diamond),
    },
    membershipOptions: permissionUi.membershipOptions,
    pinLevels: permissionUi.pinLevels,
    membershipGroupValuesById: permissionUi.membershipGroupValuesById,
    familyReunionGroups: optionFamilyReunions,
    selectedFamilyGroups,
    diamonds: optionDiamonds,
    selectedDiamonds,
    familyReunionSectionTitle: reunionSection?.label || "",
    diamondsSectionTitle: diamondSection?.label || "",
    additionalVisibilitySections,
    visibilitySelectionsByTypeCode,
    groups: optionGroups,
    selectedGroups: groupIds
      .map((id) => rawGroups.find((g) => g.groupId === id)?.name)
      .filter(Boolean),
    additionalIbos: apiSettings.additionalIbos || "",
    tags: {
      ...defaultTags,
      ...apiSettings.tags,
    },
  };
};

const buildPermissionStateFromConfig = (config) => {
  const visibilitySections = Array.isArray(config?.visibilityGroupSections)
    ? config.visibilityGroupSections
    : Array.isArray(config?.visibilityGroups)
    ? config.visibilityGroups
    : [];
  const reunionSection = findVisibilitySection(visibilitySections, "REUNION");
  const diamondSection = findVisibilitySection(visibilitySections, "DIAMOND");
  const permissionUi = buildPermissionUiFromSections(visibilitySections);
  const additionalVisibilitySections =
    buildAdditionalVisibilitySections(visibilitySections);

  const tagDefinitions = extractSettingsTagDefinitions(config);
  const tagOptionsByTypeCode = buildTagOptionsByTypeCode(tagDefinitions);
  const hasDynamicTagSchema = tagDefinitions.length > 0;

  return {
    ...DEFAULT_PERMISSION_STATE,
    membershipOptions: permissionUi.membershipOptions,
    pinLevels: permissionUi.pinLevels,
    membershipGroupValuesById: permissionUi.membershipGroupValuesById,
    familyReunionGroups: Array.isArray(reunionSection?.options)
      ? reunionSection.options.map((option) => option.label)
      : config?.familyReunionGroups
      ? config.familyReunionGroups.map((g) =>
          typeof g === "string" ? g : g.name,
        )
      : [],
    diamonds: Array.isArray(diamondSection?.options)
      ? diamondSection.options.map((option) => option.label)
      : config?.diamonds
      ? config.diamonds.map((d) => (typeof d === "string" ? d : d.name))
      : [],
    familyReunionSectionTitle: reunionSection?.label || "",
    diamondsSectionTitle: diamondSection?.label || "",
    additionalVisibilitySections,
    visibilitySelectionsByTypeCode: {},
    groups: config?.groups
      ? config.groups.map((g) => (typeof g === "string" ? g : g.name))
      : [],
    tags: {
      ...DEFAULT_PERMISSION_STATE.tags,
      tagDefinitions,
      locationOptions: hasDynamicTagSchema
        ? tagOptionsByTypeCode.LOCATION || []
        : config?.locationOptions ??
          DEFAULT_PERMISSION_STATE.tags.locationOptions,
      locations: [],
      selectedLocations: [],
      languageOptions: hasDynamicTagSchema
        ? tagOptionsByTypeCode.LANGUAGE || []
        : config?.languageOptions ??
          DEFAULT_PERMISSION_STATE.tags.languageOptions,
      languages: [],
      selectedLanguages: [],
      occupationOptions: hasDynamicTagSchema
        ? tagOptionsByTypeCode.OCCUPATION || []
        : config?.occupationOptions ??
          DEFAULT_PERMISSION_STATE.tags.occupationOptions,
      occupations: [],
      selectedOccupations: [],
      keywordOptions: hasDynamicTagSchema
        ? tagOptionsByTypeCode.KEYWORD_TAG || []
        : config?.keywordOptions ??
          DEFAULT_PERMISSION_STATE.tags.keywordOptions,
      keywords: [],
      hiddenTagOptions: hasDynamicTagSchema
        ? tagOptionsByTypeCode.HIDDEN_TAG || []
        : config?.hiddenTagOptions ??
          DEFAULT_PERMISSION_STATE.tags.hiddenTagOptions,
      hiddenTags: [],
      byTypeCode: {},
    },
  };
};

const DEFAULT_TYPE_OPTIONS = [
  { label: "Business", isUserAdded: false },
  { label: "Relationships", isUserAdded: false },
  { label: "Lifestyle", isUserAdded: false },
  { label: "Lifestyle", isUserAdded: false },
];

const DEFAULT_PERMISSION_STATE = {
  membershipPermissions: { complimentary: true, premier: true },
  pinPermissions: { ibo: true, platinum: true, emerald: true, diamond: true },
  membershipOptions: [],
  pinLevels: [],
  membershipGroupValuesById: { premier: 1, complimentary: 2 },
  familyReunionGroups: [],
  selectedFamilyGroups: [],
  diamonds: [],
  selectedDiamonds: [],
  additionalVisibilitySections: [],
  visibilitySelectionsByTypeCode: {},
  additionalIbos: "",
  tags: {
    locationOptions: [],
    locations: [],
    selectedLocations: [],
    languageOptions: [],
    languages: [],
    selectedLanguages: [],
    occupationOptions: [],
    occupations: [],
    selectedOccupations: [],
    keywordOptions: [],
    keywords: [],
    hiddenTagOptions: [],
    hiddenTags: [],
    tagDefinitions: [],
    byTypeCode: {},
  },
};

const createDefaultPublishState = () => ({
  status: "",
  publishOn: "",
  unpublishOn: "",
  unpublishEnabled: false,
  digitalDeliveryEnabled: false,
  showAsUpcomingOn: "",
  showAsCurrentUntil: "",
});

const createDefaultContentSettings = (
  baseState = DEFAULT_PERMISSION_STATE,
) => ({
  membershipPermissions: {
    ...(baseState.membershipPermissions || {}),
  },
  pinPermissions: {
    ...(baseState.pinPermissions || {}),
  },
  membershipOptions: Array.isArray(baseState?.membershipOptions)
    ? baseState.membershipOptions.map((option) => ({ ...option }))
    : [],
  pinLevels: Array.isArray(baseState?.pinLevels)
    ? baseState.pinLevels.map((option) => ({ ...option }))
    : [],
  membershipGroupValuesById: {
    ...(baseState?.membershipGroupValuesById || {}),
  },
  additionalVisibilitySections: Array.isArray(
    baseState?.additionalVisibilitySections,
  )
    ? baseState.additionalVisibilitySections.map((section) => ({
        ...section,
        options: Array.isArray(section?.options) ? [...section.options] : [],
      }))
    : [],
  visibilitySelectionsByTypeCode: {
    ...(baseState?.visibilitySelectionsByTypeCode || {}),
  },
  familyReunionGroups: [...(baseState.familyReunionGroups || [])],
  selectedFamilyGroups: [...(baseState.selectedFamilyGroups || [])],
  diamonds: [...(baseState.diamonds || [])],
  selectedDiamonds: [...(baseState.selectedDiamonds || [])],
  additionalIbos: baseState.additionalIbos || "",
  tags: {
    ...(baseState.tags || {}),
    tagDefinitions: Array.isArray(baseState?.tags?.tagDefinitions)
      ? baseState.tags.tagDefinitions.map((definition) => ({
          ...definition,
          options: Array.isArray(definition.options)
            ? [...definition.options]
            : [],
        }))
      : [],
    locationOptions: [...(baseState?.tags?.locationOptions || [])],
    locations: [...(baseState?.tags?.locations || [])],
    selectedLocations: [...(baseState?.tags?.selectedLocations || [])],
    languageOptions: [...(baseState?.tags?.languageOptions || [])],
    languages: [...(baseState?.tags?.languages || [])],
    selectedLanguages: [...(baseState?.tags?.selectedLanguages || [])],
    occupationOptions: [...(baseState?.tags?.occupationOptions || [])],
    occupations: [...(baseState?.tags?.occupations || [])],
    selectedOccupations: [...(baseState?.tags?.selectedOccupations || [])],
    keywordOptions: [...(baseState?.tags?.keywordOptions || [])],
    keywords: [...(baseState?.tags?.keywords || [])],
    hiddenTagOptions: [...(baseState?.tags?.hiddenTagOptions || [])],
    hiddenTags: [...(baseState?.tags?.hiddenTags || [])],
    byTypeCode: { ...(baseState?.tags?.byTypeCode || {}) },
  },
});

const normalizePublishStatus = (status) => {
  if (status === "Upcoming") {
    return "Upcoming";
  }

  if (status === "Archived") {
    return "Archived";
  }

  if (status === "In Review") {
    return "In Review";
  }

  return "Published";
};

const getPlaylistDisplayStatus = (mode, publishStatus) => {
  if (mode === "draft") {
    return "In Review";
  }

  if (publishStatus === "Upcoming") {
    return "Upcoming";
  }

  if (publishStatus === "Archived") {
    return "Archived";
  }

  if (publishStatus === "In Review") {
    return "In Review";
  }

  return "Available";
};

const getPlaylistWorkflowStatus = (mode, publishStatus) => {
  if (mode === "draft") {
    return "scheduled";
  }

  if (publishStatus === "Archived") {
    return "archived";
  }

  if (publishStatus === "In Review") {
    return "in-review";
  }

  if (publishStatus === "Upcoming" || publishStatus === "Scheduled") {
    return "scheduled";
  }

  return "published";
};

const BASE_PLAYLIST_FILTER_SECTIONS = [
  {
    label: "Type",
    options: [
      { label: "WWG", value: "wwg" },
      { label: "3rd Party", value: "3rd" },
    ],
  },
  {
    label: "Theme",
    options: [],
  },
  {
    label: "Language",
    options: [],
  },
  {
    label: "Status",
    options: [
      { label: "Published", value: "published" },
      { label: "Scheduled", value: "scheduled" },
      { label: "In Review", value: "in-review" },
      { label: "Archived", value: "archived" },
    ],
  },
];

const BASE_AUDIO_FILTER_SECTIONS = [
  {
    label: "Type",
    options: [
      { label: "WWG", value: "wwg" },
      { label: "3rd Party", value: "3rd" },
    ],
  },
  {
    label: "Category",
    options: [
      { label: "Audio", value: "Audio" },
      { label: "Video", value: "Video" },
      { label: "Music", value: "Music" },
    ],
  },
  {
    label: "Language",
    options: [],
  },
];

const SORT_OPTIONS = [
  { label: "Date: Newest", value: "date-newest" },
  { label: "Date: Oldest", value: "date-oldest" },
  { label: "Audio: A to Z", value: "audio-asc" },
  { label: "Audio: Z to A", value: "audio-desc" },
];

const PLAYLIST_SORT_OPTIONS = [
  { label: "Date: Newest", value: "date-newest" },
  { label: "Date: Oldest", value: "date-oldest" },
  { label: "Playlist: A to Z", value: "playlist-asc" },
  { label: "Playlist: Z to A", value: "playlist-desc" },
];

const EMPTY_PLAYLISTS = [];
const EMPTY_AUDIO_LIBRARY = [];

const toAudioSelectionId = (audioId) => String(audioId);

const playlistAudioMatchesLibraryAudio = (playlistAudio, libraryAudio) =>
  toAudioSelectionId(playlistAudio.mediaItemId) ===
    toAudioSelectionId(libraryAudio.mediaItemId) ||
  (playlistAudio.sku &&
    libraryAudio.sku &&
    String(playlistAudio.sku) === String(libraryAudio.sku));
const normalizeTagToken = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const toTagFilterOptions = (contentSettingsConfig, fallbackTags = []) => {
  const definitions = extractSettingsTagDefinitions(contentSettingsConfig);
  const optionsByTypeCode = buildTagOptionsByTypeCode(definitions);
  const keywordOptions = Array.isArray(optionsByTypeCode.KEYWORD_TAG)
    ? optionsByTypeCode.KEYWORD_TAG
    : fallbackTags;

  return Array.from(
    new Map(
      keywordOptions
        .map((option) => String(option || "").trim())
        .filter(Boolean)
        .map((label) => [label.toLowerCase(), { label, value: label }]),
    ).values(),
  );
};

const toLanguageFilterOptions = (
  contentSettingsConfig,
  fallbackLanguages = [],
) => {
  const definitions = extractSettingsTagDefinitions(contentSettingsConfig);
  const optionsByTypeCode = buildTagOptionsByTypeCode(definitions);
  const languageOptionsSource = Array.isArray(optionsByTypeCode.LANGUAGE)
    ? optionsByTypeCode.LANGUAGE
    : fallbackLanguages;

  return Array.from(
    new Map(
      languageOptionsSource
        .map((option) => String(option || "").trim())
        .filter(Boolean)
        .map((label) => [
          label.toLowerCase(),
          { label, value: label.toLowerCase() },
        ]),
    ).values(),
  );
};

const appendTagFilterSection = (sections, tagOptions) => {
  const tagSection = {
    label: "Tag",
    options: tagOptions,
  };

  if (sections.length === 0) {
    return [tagSection];
  }

  return [...sections.slice(0, 3), tagSection, ...sections.slice(3)];
};

const withLanguageAndTagSections = ({
  baseSections,
  languageOptions,
  tagOptions,
}) => {
  const sectionsWithLanguage = baseSections.map((section) =>
    section.label === "Language"
      ? {
          ...section,
          options: languageOptions,
        }
      : section,
  );

  return appendTagFilterSection(sectionsWithLanguage, tagOptions);
};

function sortAudios(items, sortBy) {
  const sorted = [...items];

  if (sortBy === "date-newest") {
    sorted.sort((a, b) => new Date(b.date) - new Date(a.date));
  }

  if (sortBy === "date-oldest") {
    sorted.sort((a, b) => new Date(a.date) - new Date(b.date));
  }

  if (sortBy === "audio-asc") {
    sorted.sort((a, b) => a.title.localeCompare(b.title));
  }

  if (sortBy === "audio-desc") {
    sorted.sort((a, b) => b.title.localeCompare(a.title));
  }

  return sorted;
}

function sortPlaylists(items, sortBy) {
  const sorted = [...items];

  if (sortBy === "date-newest") {
    sorted.sort((a, b) => new Date(b.date) - new Date(a.date));
  }

  if (sortBy === "date-oldest") {
    sorted.sort((a, b) => new Date(a.date) - new Date(b.date));
  }

  if (sortBy === "playlist-asc") {
    sorted.sort((a, b) => a.playlistTitle.localeCompare(b.playlistTitle));
  }

  if (sortBy === "playlist-desc") {
    sorted.sort((a, b) => b.playlistTitle.localeCompare(a.playlistTitle));
  }

  return sorted;
}

function normalizeThumbnailStateFromApi(thumbnailState, fallbackUrl = "") {
  const mode = thumbnailState?.mode;
  const uploadedSrc =
    thumbnailState?.src || thumbnailState?.customImageUrl || fallbackUrl || "";

  if ((mode === "uploaded" || mode === "custom") && uploadedSrc) {
    return { mode: "uploaded", src: uploadedSrc };
  }

  if (mode === "audios") {
    return { mode: "audios", src: "" };
  }

  return { mode: "none", src: "" };
}

function buildCoverArtData(thumbnailState, playlistAudios) {
  const hasPlaylistAudios = playlistAudios.length > 0;
  const uploadedSrc = thumbnailState?.src || thumbnailState?.customImageUrl;

  if (
    (thumbnailState?.mode === "uploaded" ||
      thumbnailState?.mode === "custom") &&
    uploadedSrc
  ) {
    return {
      thumbnails: [uploadedSrc],
      overflowCount: 0,
      hasPlaylistAudios,
    };
  }

  if (thumbnailState.mode === "audios" && hasPlaylistAudios) {
    // Match the buildAudioGridImage logic
    if (playlistAudios.length <= 4) {
      // Show all audios (up to 4)
      return {
        thumbnails: playlistAudios
          .slice(0, 4)
          .map((item) => item.thumbnailPath),
        overflowCount: 0,
        hasPlaylistAudios,
      };
    }

    // More than 4: show first 3 + overflow on 4th
    return {
      thumbnails: playlistAudios.slice(0, 3).map((item) => item.thumbnailPath),
      overflowCount: playlistAudios.length - 3,
      hasPlaylistAudios,
    };
  }

  return { thumbnails: [], overflowCount: 0, hasPlaylistAudios };
}

/**
 * Build a canvas image of the audio thumbnail grid (2x2)
 * Captures what PlaylistCoverArt renders visually
 * @param {Array} thumbnails - array of thumbnail URLs
 * @returns {Promise<Blob>} - canvas image as a blob
 */
async function buildAudioGridImage(thumbnails) {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  // Set canvas size: 400x400 (200x200 per tile)
  canvas.width = 400;
  canvas.height = 400;

  // Fill background with light gray (matching typical placeholder)
  ctx.fillStyle = "#f5f5f5";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const tileSize = 200;
  const positions = [
    { x: 0, y: 0 },
    { x: tileSize, y: 0 },
    { x: 0, y: tileSize },
    { x: tileSize, y: tileSize },
  ];

  // Calculate overflow
  const validThumbnails = (thumbnails || []).filter(Boolean);
  const hasOverflow = validThumbnails.length > 4;
  const overflowCount = validThumbnails.length - 3;
  const visibleThumbnails = hasOverflow
    ? validThumbnails.slice(0, 3)
    : validThumbnails.slice(0, 4);

  for (let i = 0; i < 4; i++) {
    const pos = positions[i];
    const isOverflowTile = hasOverflow && i === 3;

    if (isOverflowTile) {
      // Draw overflow tile with "+n" text
      ctx.fillStyle = "#d9d9d9";
      ctx.fillRect(pos.x, pos.y, tileSize, tileSize);

      // Draw the overflow text
      ctx.fillStyle = "#ffffff";
      ctx.font = "bold 60px Arial";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(
        `+${overflowCount}`,
        pos.x + tileSize / 2,
        pos.y + tileSize / 2,
      );
    } else if (visibleThumbnails[i]) {
      try {
        // Fetch and draw the thumbnail image
        const response = await fetch(visibleThumbnails[i]);
        if (response.ok) {
          const blob = await response.blob();
          const img = new Image();
          img.crossOrigin = "anonymous";

          await new Promise((resolve, reject) => {
            img.onload = () => {
              ctx.drawImage(img, pos.x, pos.y, tileSize, tileSize);
              resolve();
            };
            img.onerror = () => {
              // If image fails, just leave it as gray
              resolve();
            };
            img.src = URL.createObjectURL(blob);
          });
        }
      } catch (err) {
        // If fetch fails, just leave it as gray placeholder
        console.warn(
          "[Playlists] Failed to load thumbnail for grid:",
          visibleThumbnails[i],
          err,
        );
      }
    } else {
      // Draw a lighter placeholder for empty slots
      ctx.fillStyle = "#e8e8e8";
      ctx.fillRect(pos.x, pos.y, tileSize, tileSize);

      // Add subtle border
      ctx.strokeStyle = "#d0d0d0";
      ctx.lineWidth = 1;
      ctx.strokeRect(pos.x, pos.y, tileSize, tileSize);
    }
  }

  // Return canvas as PNG blob
  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(blob || new Blob([], { type: "image/png" }));
    }, "image/png");
  });
}

export function Playlists({
  initialContentSettingsConfig = null,
  initialPlaylists = EMPTY_PLAYLISTS,
  audioLibrary: initialAudioLibrary = EMPTY_AUDIO_LIBRARY,
  disableInitialFetch = false,
}) {
  const safeInitialPlaylists = Array.isArray(initialPlaylists)
    ? initialPlaylists
    : [];
  const safeInitialAudioLibrary = Array.isArray(initialAudioLibrary)
    ? initialAudioLibrary
    : [];

  const [isCreateView, setIsCreateView] = useState(false);
  const [isSelectingAudios, setIsSelectingAudios] = useState(false);
  const [editingPlaylistId, setEditingPlaylistId] = useState(null);
  const [isEditingBaselinesReady, setIsEditingBaselinesReady] = useState(false);
  const [editingWasDraft, setEditingWasDraft] = useState(false);
  const [apiError, setApiError] = useState("");
  const [archivingPlaylistId, setArchivingPlaylistId] = useState(null);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [thumbnailUploading, setThumbnailUploading] = useState(false);
  const [duplicatingPlaylistId, setDuplicatingPlaylistId] = useState(null);

  const [playlists, setPlaylists] = useState(safeInitialPlaylists);
  const [audioLibrary, setAudioLibrary] = useState(safeInitialAudioLibrary);
  const [playlistSearchQuery, setPlaylistSearchQuery] = useState("");
  const [playlistSortBy, setPlaylistSortBy] = useState("date-newest");
  const [playlistFilters, setPlaylistFilters] = useState({
    Status: ["published"],
  });

  const [playlistInfo, setPlaylistInfo] = useState({
    title: "",
    location: LOCATION_OPTIONS[0],
    type: "Business",
  });
  const [playlistInfoBaseline, setPlaylistInfoBaseline] = useState(null);
  const [playlistAudiosBaseline, setPlaylistAudiosBaseline] = useState(null);
  const [thumbnailStateBaseline, setThumbnailStateBaseline] = useState(null);
  const [publishStateBaseline, setPublishStateBaseline] = useState(null);
  const [contentSettingsBaseline, setContentSettingsBaseline] = useState(null);
  const [playlistTitleError, setPlaylistTitleError] = useState(false);
  const [attemptedPlaylistSave, setAttemptedPlaylistSave] = useState(false);
  const [typeOptions, setTypeOptions] = useState(DEFAULT_TYPE_OPTIONS);
  const [pendingTypeDeletions, setPendingTypeDeletions] = useState([]);

  const [playlistAudios, setPlaylistAudios] = useState([]);
  const [playlistAudioSearchQuery, setPlaylistAudioSearchQuery] = useState("");

  const [thumbnailState, setThumbnailState] = useState({
    mode: "none",
    src: "",
  });
  const [thumbnailTempFilename, setThumbnailTempFilename] = useState(null);
  const [thumbnailPrevUrl, setThumbnailPrevUrl] = useState("");
  const [pendingImageMove, setPendingImageMove] = useState(null);

  const [publishState, setPublishState] = useState({
    ...createDefaultPublishState(),
  });

  const [contentSettingsConfig, setContentSettingsConfig] = useState(
    initialContentSettingsConfig,
  );

  const observedAudioLanguages = useMemo(
    () =>
      audioLibrary
        .map((item) => String(item?.language || "").trim())
        .filter(Boolean),
    [audioLibrary],
  );

  const observedAudioTags = useMemo(
    () =>
      audioLibrary
        .flatMap((item) => (Array.isArray(item?.tags) ? item.tags : []))
        .map((tag) => String(tag || "").trim())
        .filter(Boolean),
    [audioLibrary],
  );

  const tagFilterOptions = useMemo(
    () => toTagFilterOptions(contentSettingsConfig, observedAudioTags),
    [contentSettingsConfig, observedAudioTags],
  );

  const languageFilterOptions = useMemo(
    () =>
      toLanguageFilterOptions(contentSettingsConfig, observedAudioLanguages),
    [contentSettingsConfig, observedAudioLanguages],
  );

  const playlistThemeFilterOptions = useMemo(() => {
    const themeLabels = [
      ...DEFAULT_TYPE_OPTIONS.map((option) => option.label),
      ...typeOptions.map((option) =>
        typeof option === "string" ? option : option?.label,
      ),
      ...playlists.map((playlist) => playlist?.type),
    ]
      .map((label) => String(label || "").trim())
      .filter(Boolean);

    return Array.from(
      new Map(
        themeLabels.map((label) => [
          label.toLowerCase(),
          { label, value: label },
        ]),
      ).values(),
    );
  }, [playlists, typeOptions]);

  const playlistFilterSections = useMemo(
    () =>
      withLanguageAndTagSections({
        baseSections: BASE_PLAYLIST_FILTER_SECTIONS.map((section) =>
          section.label === "Theme"
            ? {
                ...section,
                options: playlistThemeFilterOptions,
              }
            : section,
        ),
        languageOptions: languageFilterOptions,
        tagOptions: tagFilterOptions,
      }),
    [languageFilterOptions, playlistThemeFilterOptions, tagFilterOptions],
  );

  const audioFilterSections = useMemo(
    () =>
      withLanguageAndTagSections({
        baseSections: BASE_AUDIO_FILTER_SECTIONS,
        languageOptions: languageFilterOptions,
        tagOptions: tagFilterOptions,
      }),
    [languageFilterOptions, tagFilterOptions],
  );

  const basePermissionState = useMemo(
    () => buildPermissionStateFromConfig(contentSettingsConfig),
    [contentSettingsConfig],
  );

  const [contentSettings, setContentSettings] = useState(() =>
    buildPermissionStateFromConfig(initialContentSettingsConfig),
  );

  const loadPlaylists = useCallback(async () => {
    setIsLoadingList(true);
    try {
      const startTime = Date.now();
      const response = await fetchDreamcastPlaylists();
      setPlaylists(Array.isArray(response?.data) ? response.data : []);

      // Ensure minimum 500ms display time for loader visibility
      const elapsed = Date.now() - startTime;
      const remainingTime = Math.max(0, 500 - elapsed);
      if (remainingTime > 0) {
        await new Promise((resolve) => setTimeout(resolve, remainingTime));
      }
    } finally {
      setIsLoadingList(false);
    }
  }, []);

  const loadAudioLibrary = useCallback(async () => {
    const response = await fetchDreamcastMedia();
    const raw = Array.isArray(response?.data) ? response.data : [];
    const normalized = raw.map((it) => {
      const mediaTypeFromDetail = it?.detailState?.mediaType;
      const fallback = it?.category || mediaTypeFromDetail || "Audio";
      const thumbnailState = normalizeThumbnailStateFromApi(
        it?.thumbnailState,
        it?.thumbnailPath,
      );

      return {
        ...it,
        mediaTypeCode: it?.mediaTypeCode || fallback,
        thumbnailState,
      };
    });
    setAudioLibrary(normalized);
    return normalized;
  }, []);

  const loadPlaylistTypes = useCallback(async () => {
    const response = await fetchDreamcastPlaylistTypes();
    const types = Array.isArray(response?.data) ? response.data : [];
    setTypeOptions(types.length > 0 ? types : DEFAULT_TYPE_OPTIONS);
  }, []);

  useEffect(() => {
    if (disableInitialFetch || initialContentSettingsConfig) {
      return;
    }

    let isSubscribed = true;

    fetchDreamcastContentSettingsConfig()
      .then((response) => {
        const payload = response?.data || response;
        if (!isSubscribed || !payload) {
          return;
        }

        setContentSettingsConfig(payload);
      })
      .catch(() => {
        // Keep fallbacks when config fetch is unavailable.
      });

    return () => {
      isSubscribed = false;
    };
  }, [disableInitialFetch, initialContentSettingsConfig]);

  // Refetch config when a new tag is created
  const handleTagCreated = useCallback(() => {
    fetchDreamcastContentSettingsConfig()
      .then((response) => {
        const payload = response?.data || response;
        if (payload) {
          setContentSettingsConfig(payload);
        }
      })
      .catch(() => {
        // Keep fallbacks when config fetch is unavailable.
      });
  }, []);

  useEffect(() => {
    setContentSettings((current) => {
      const baseTags = basePermissionState?.tags || {};
      const currentTags = current?.tags || {};
      const baseTagDefinitions = Array.isArray(baseTags.tagDefinitions)
        ? baseTags.tagDefinitions
        : [];
      const baseFamilyReunionGroups = Array.isArray(
        basePermissionState?.familyReunionGroups,
      )
        ? basePermissionState.familyReunionGroups
        : [];
      const baseDiamonds = Array.isArray(basePermissionState?.diamonds)
        ? basePermissionState.diamonds
        : [];
      const baseGroups = Array.isArray(basePermissionState?.groups)
        ? basePermissionState.groups
        : [];
      const baseAdditionalVisibilitySections = Array.isArray(
        basePermissionState?.additionalVisibilitySections,
      )
        ? basePermissionState.additionalVisibilitySections
        : [];
      const currentVisibilitySelectionsByTypeCode =
        current?.visibilitySelectionsByTypeCode &&
        typeof current.visibilitySelectionsByTypeCode === "object"
          ? current.visibilitySelectionsByTypeCode
          : {};
      const baseMembershipOptions = Array.isArray(
        basePermissionState?.membershipOptions,
      )
        ? basePermissionState.membershipOptions
        : [];
      const basePinLevels = Array.isArray(basePermissionState?.pinLevels)
        ? basePermissionState.pinLevels
        : [];
      const baseMembershipGroupValuesById =
        basePermissionState?.membershipGroupValuesById &&
        typeof basePermissionState.membershipGroupValuesById === "object"
          ? basePermissionState.membershipGroupValuesById
          : {};

      return {
        ...basePermissionState,
        ...current,
        familyReunionGroups: baseFamilyReunionGroups,
        diamonds: baseDiamonds,
        groups: baseGroups,
        additionalVisibilitySections: baseAdditionalVisibilitySections,
        visibilitySelectionsByTypeCode: currentVisibilitySelectionsByTypeCode,
        membershipOptions: baseMembershipOptions,
        pinLevels: basePinLevels,
        membershipGroupValuesById: baseMembershipGroupValuesById,
        familyReunionSectionTitle:
          basePermissionState?.familyReunionSectionTitle ||
          current?.familyReunionSectionTitle,
        diamondsSectionTitle:
          basePermissionState?.diamondsSectionTitle ||
          current?.diamondsSectionTitle,
        tags: {
          ...baseTags,
          ...currentTags,
          locationOptions:
            Array.isArray(baseTags.locationOptions) &&
            baseTags.locationOptions.length > 0
              ? baseTags.locationOptions
              : currentTags.locationOptions || [],
          languageOptions:
            Array.isArray(baseTags.languageOptions) &&
            baseTags.languageOptions.length > 0
              ? baseTags.languageOptions
              : currentTags.languageOptions || [],
          occupationOptions:
            Array.isArray(baseTags.occupationOptions) &&
            baseTags.occupationOptions.length > 0
              ? baseTags.occupationOptions
              : currentTags.occupationOptions || [],
          keywordOptions:
            Array.isArray(baseTags.keywordOptions) &&
            baseTags.keywordOptions.length > 0
              ? baseTags.keywordOptions
              : currentTags.keywordOptions || [],
          hiddenTagOptions:
            Array.isArray(baseTags.hiddenTagOptions) &&
            baseTags.hiddenTagOptions.length > 0
              ? baseTags.hiddenTagOptions
              : currentTags.hiddenTagOptions || [],
          // Keep DB-provided tag schema once available.
          tagDefinitions:
            baseTagDefinitions.length > 0
              ? baseTagDefinitions
              : currentTags.tagDefinitions || [],
        },
      };
    });
  }, [basePermissionState]);

  useEffect(() => {
    if (disableInitialFetch) {
      return;
    }

    loadPlaylists().catch((err) =>
      setApiError(err.message || "Failed to load playlists."),
    );
    loadAudioLibrary().catch((err) =>
      setApiError(err.message || "Failed to load audio library."),
    );
    loadPlaylistTypes().catch(() => {
      // keep DEFAULT_TYPE_OPTIONS if config endpoint is unavailable
    });
  }, [disableInitialFetch, loadPlaylists, loadAudioLibrary, loadPlaylistTypes]);

  useEffect(() => {
    if (!disableInitialFetch) {
      return;
    }

    setPlaylists(safeInitialPlaylists);
    setAudioLibrary(safeInitialAudioLibrary);
  }, [disableInitialFetch, safeInitialPlaylists, safeInitialAudioLibrary]);

  const [selectionSearchQuery, setSelectionSearchQuery] = useState("");
  const [selectionSortBy, setSelectionSortBy] = useState("date-newest");
  const [selectionFilters, setSelectionFilters] = useState({});
  const [selectedAudioIds, setSelectedAudioIds] = useState([]);

  const filteredPlaylists = useMemo(() => {
    const normalizedQuery = playlistSearchQuery.trim().toLowerCase();
    const hasSearchQuery = normalizedQuery.length > 0;

    const next = playlists.filter((playlist) => {
      // Only apply search filter if user has actually typed something
      if (hasSearchQuery) {
        const tagsString = Array.isArray(playlist.tags)
          ? playlist.tags.join(" ")
          : "";
        const playlistHiddenTagsString = Array.isArray(playlist.contentSettings?.tags?.hiddenTags)
          ? playlist.contentSettings.tags.hiddenTags.join(" ")
          : "";
        const searchable =
          `${playlist.playlistTitle} ${playlist.location} ${playlist.type} ${tagsString} ${playlistHiddenTagsString}`.toLowerCase();

        if (!searchable.includes(normalizedQuery)) {
          return false;
        }
      }

      const typeFilter = playlistFilters.Type || [];
      if (
        typeFilter.length > 0 &&
        !typeFilter.includes(playlist.typeGroup || "wwg")
      ) {
        return false;
      }

      const themeFilter = playlistFilters.Theme || [];
      if (
        themeFilter.length > 0 &&
        !themeFilter.some(
          (theme) =>
            String(theme).toLowerCase() ===
            String(playlist.type || "").toLowerCase(),
        )
      ) {
        return false;
      }

      const languageFilter = playlistFilters.Language || [];
      if (languageFilter.length > 0) {
        const normalizedPlaylistLanguage = String(
          playlist.language || "english",
        ).toLowerCase();
        const selectedLanguages = languageFilter.map((value) =>
          String(value || "").toLowerCase(),
        );
        if (!selectedLanguages.includes(normalizedPlaylistLanguage)) {
          return false;
        }
      }

      const tagFilter = playlistFilters.Tag || [];
      if (tagFilter.length > 0) {
        const itemTags = Array.isArray(playlist.tags) ? playlist.tags : [];
        const normalizedItemTags = itemTags.map(normalizeTagToken);
        const hasMatch = tagFilter.some((tag) =>
          normalizedItemTags.includes(normalizeTagToken(tag)),
        );
        if (!hasMatch) {
          return false;
        }
      }

      const statusFilter = playlistFilters.Status || [];
      if (
        statusFilter.length > 0 &&
        !statusFilter.includes(playlist.workflowStatus || "published")
      ) {
        return false;
      }

      return true;
    });

    return sortPlaylists(next, playlistSortBy);
  }, [playlistFilters, playlistSearchQuery, playlistSortBy, playlists]);

  const playlistFiltersHaveSelection = Object.keys(playlistFilters || {}).some(
    (key) =>
      key === "Status"
        ? false
        : Array.isArray(playlistFilters[key]) &&
          playlistFilters[key].length > 0,
  );

  const playlistThemeSelected = Array.isArray(playlistFilters?.Theme)
    ? playlistFilters.Theme.length > 0
    : false;

  const playlistSearchBarNoResults =
    Array.isArray(filteredPlaylists) &&
    filteredPlaylists.length === 0 &&
    String(playlistSearchQuery || "").trim() !== "";

  const playlistThemeNoResults =
    Array.isArray(filteredPlaylists) &&
    filteredPlaylists.length === 0 &&
    playlistThemeSelected;

  const playlistFiltersNoResults =
    Array.isArray(filteredPlaylists) &&
    filteredPlaylists.length === 0 &&
    playlistFiltersHaveSelection;

  const selectableAudios = useMemo(() => {
    const normalizedQuery = selectionSearchQuery.trim().toLowerCase();
    const hasSearchQuery = normalizedQuery.length > 0;

    const filtered = audioLibrary.filter((item) => {
      if (String(item.workflowStatus || "").toLowerCase() !== "published") {
        return false;
      }

      // Only apply search filter if user has actually typed something
      if (hasSearchQuery) {
        const tagsString = Array.isArray(item.tags)
          ? item.tags.join(" ")
          : "";
        const itemHiddenTagsString = Array.isArray(item.contentSettings?.tags?.hiddenTags)
          ? item.contentSettings.tags.hiddenTags.join(" ")
          : "";
        const searchable =
          `${item.title} ${item.contributorName} ${item.sku} ${tagsString} ${itemHiddenTagsString}`.toLowerCase();
        if (!searchable.includes(normalizedQuery)) {
          return false;
        }
      }

      const typeFilter = selectionFilters.Type || [];
      if (typeFilter.length > 0 && !typeFilter.includes(item.typeGroup)) {
        return false;
      }

      if (!matchesCategoryFilter(item, selectionFilters.Category)) {
        return false;
      }

      const languageFilter = selectionFilters.Language || [];
      if (languageFilter.length > 0) {
        const normalizedItemLanguage = String(
          item.language || "",
        ).toLowerCase();
        const selectedLanguages = languageFilter.map((value) =>
          String(value || "").toLowerCase(),
        );
        if (!selectedLanguages.includes(normalizedItemLanguage)) {
          return false;
        }
      }

      const tagFilter = selectionFilters.Tag || [];
      if (tagFilter.length > 0) {
        const itemTags = Array.isArray(item.tags) ? item.tags : [];
        const normalizedItemTags = itemTags.map(normalizeTagToken);
        const hasMatchingTag = tagFilter.some((tag) =>
          normalizedItemTags.includes(normalizeTagToken(tag)),
        );
        if (!hasMatchingTag) {
          return false;
        }
      }

      const statusFilter = selectionFilters.Status || [];
      if (
        statusFilter.length > 0 &&
        !statusFilter.includes(item.workflowStatus)
      ) {
        return false;
      }

      return true;
    });

    return sortAudios(filtered, selectionSortBy);
  }, [audioLibrary, selectionFilters, selectionSearchQuery, selectionSortBy, contentSettings?.tags?.hiddenTags]);

  const selectionNoResults =
    Array.isArray(selectableAudios) &&
    selectableAudios.length === 0 &&
    String(selectionSearchQuery || "").trim() !== "";

  const selectionFiltersHaveSelection = Object.keys(
    selectionFilters || {},
  ).some(
    (key) =>
      Array.isArray(selectionFilters[key]) && selectionFilters[key].length > 0,
  );

  const selectionCategorySelected = Array.isArray(selectionFilters?.Category)
    ? selectionFilters.Category.length > 0
    : false;

  const selectionSearchBarNoResults =
    Array.isArray(selectableAudios) &&
    selectableAudios.length === 0 &&
    (String(selectionSearchQuery || "").trim() !== "" ||
      (selectionFiltersHaveSelection && !selectionCategorySelected));

  const selectionFiltersNoResults =
    Array.isArray(selectableAudios) &&
    selectableAudios.length === 0 &&
    selectionFiltersHaveSelection;

  const selectionCategoryNoResults =
    Array.isArray(selectableAudios) &&
    selectableAudios.length === 0 &&
    selectionCategorySelected;

  const coverArtData = useMemo(
    () => buildCoverArtData(thumbnailState, playlistAudios),
    [thumbnailState, playlistAudios],
  );

  // When editing, automatically set isEditingBaselinesReady when form state settles
  // We know it's settled when current state matches the baselines (form is clean)
  // This prevents buttons from being enabled during initial render
  useEffect(() => {
    if (
      editingPlaylistId &&
      playlistInfoBaseline &&
      Array.isArray(playlistAudiosBaseline) &&
      thumbnailStateBaseline &&
      publishStateBaseline &&
      contentSettingsBaseline &&
      !isEditingBaselinesReady
    ) {
      // Check if current state matches baselines (form is in clean state)
      const isFormSettled =
        playlistInfo.title === playlistInfoBaseline.title &&
        playlistInfo.location === playlistInfoBaseline.location &&
        playlistInfo.type === playlistInfoBaseline.type &&
        JSON.stringify(playlistAudios) ===
          JSON.stringify(playlistAudiosBaseline) &&
        JSON.stringify(thumbnailState) ===
          JSON.stringify(thumbnailStateBaseline) &&
        JSON.stringify(publishState) ===
          JSON.stringify(publishStateBaseline) &&
        JSON.stringify(contentSettings) ===
          JSON.stringify(contentSettingsBaseline);

      if (isFormSettled) {
        // Form has settled to match baseline - ready for dirty detection
        setIsEditingBaselinesReady(true);
      }
    }
  }, [
    editingPlaylistId,
    playlistInfoBaseline,
    playlistAudiosBaseline,
    thumbnailStateBaseline,
    publishStateBaseline,
    contentSettingsBaseline,
    playlistInfo,
    playlistAudios,
    thumbnailState,
    publishState,
    contentSettings,
    isEditingBaselinesReady,
  ]);

  const resetCreateView = () => {
    setEditingPlaylistId(null);
    setIsEditingBaselinesReady(false);
    setEditingWasDraft(false);
    setPendingTypeDeletions([]);
    setApiError("");
    setPlaylistTitleError(false);
    setAttemptedPlaylistSave(false);
    setPlaylistInfo({
      title: "",
      location: LOCATION_OPTIONS[0],
      type: "Business",
    });
    setPlaylistInfoBaseline(null);
    setPlaylistAudios([]);
    setPlaylistAudiosBaseline(null);
    setPlaylistAudioSearchQuery("");
    setThumbnailState({ mode: "none", src: "" });
    setThumbnailStateBaseline(null);
    setThumbnailTempFilename(null);
    setThumbnailPrevUrl("");
    setPendingImageMove(null);
    setPublishState(createDefaultPublishState());
    setPublishStateBaseline(null);
    const defaultContentSettings = createDefaultContentSettings(basePermissionState);
    setContentSettings(defaultContentSettings);
    setContentSettingsBaseline(defaultContentSettings);

    setSelectionSearchQuery("");
    setSelectionSortBy("date-newest");
    setSelectionFilters({});
    setSelectedAudioIds([]);
    setIsSelectingAudios(false);
  };

  // If selection mode has category-driven no results, we'll show the page-level message
  // below where the selection list would render. We don't need to change existing
  // selection list rendering here; the flag is computed for usage in renderSelectionMode if needed.

  const handleOpenCreateView = () => {
    resetCreateView();
    setIsCreateView(true);
  };

  const handleEditPlaylist = useCallback(
    async (playlistId) => {
      setEditingPlaylistId(playlistId);
      // Reset the baselines ready flag - will be set again by useEffect when form settles
      setIsEditingBaselinesReady(false);
      // IMPORTANT: Clear old baselines AND form data immediately to prevent stale comparisons
      setPlaylistInfoBaseline(null);
      setPlaylistAudiosBaseline(null);
      setThumbnailStateBaseline(null);
      setPublishStateBaseline(null);
      setContentSettingsBaseline(null);
      
      let target = playlists.find((p) => p.playlistId === playlistId);
      try {
        setApiError("");
        const detailResponse = await fetchDreamcastPlaylistById(playlistId);
        target = detailResponse?.data || target;
      } catch (error) {
        setApiError(error.message || "Failed to load playlist details.");
        setEditingPlaylistId(null);
      }

      if (!target) {
        setEditingPlaylistId(null);
        return;
      }
      setEditingWasDraft(Boolean(target.isDraft));

      setPlaylistInfo({
        title: target.playlistTitle || "",
        location: target.formLocation || LOCATION_OPTIONS[0],
        type: target.type || DEFAULT_TYPE_OPTIONS[0].label,
      });

      setTypeOptions((current) => {
        const hasType = current.some(
          (option) =>
            option.label.toLowerCase() === (target.type || "").toLowerCase(),
        );

        if (target.type && !hasType) {
          return [...current, { label: target.type, isUserAdded: true }];
        }

        return current;
      });

      const sortedAudios = Array.isArray(target.playlistMedia)
        ? [...target.playlistMedia].sort(
            (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
          )
        : [];
      // Restore state dropped in a merge: without this, editing a playlist
      // shows an empty audio list and saving would wipe its media.
      const normalizedPlaylistAudios = sortedAudios.map((it) => {
        const mediaTypeFromDetail = it?.detailState?.mediaType;
        const fallback =
          it?.mediaTypeCode || it?.category || mediaTypeFromDetail || "Audio";

        return {
          ...it,
          date: it.publishOn,
          mediaTypeCode: it?.mediaTypeCode || fallback,
          thumbnailState: normalizeThumbnailStateFromApi(
            it?.thumbnailState,
            it?.thumbnailPath,
          ),
        };
      });
      setPlaylistAudios(normalizedPlaylistAudios);
      setPlaylistAudioSearchQuery("");
      const normalizedThumbnailState = normalizeThumbnailStateFromApi(
        target.thumbnailState,
        target.thumbnailPath,
      );
      setThumbnailState(normalizedThumbnailState);
      setThumbnailTempFilename(null);
      setThumbnailPrevUrl(
        target.thumbnailPath || normalizedThumbnailState.src || "",
      );

      const publishDefaults = createDefaultPublishState();
      const sourcePublish = target.publishState || {};
      const normalizedPublishState = {
        ...publishDefaults,
        ...sourcePublish,
        status: normalizePublishStatus(sourcePublish.status || target.status),
      };

      setPublishState(normalizedPublishState);

      setContentSettings(
        normalizeContentSettingsFromApi(target.contentSettings, {
          ...contentSettingsConfig,
          _processedLists: basePermissionState,
        }) || basePermissionState,
      );

      const normalizedContentSettings = normalizeContentSettingsFromApi(
        target.contentSettings,
        {
          ...contentSettingsConfig,
          _processedLists: basePermissionState,
        },
      ) || basePermissionState;

      setSelectionSearchQuery("");
      setSelectionSortBy("date-newest");
      setSelectionFilters({});
      setIsSelectingAudios(false);

      // Store baseline state for edit mode
      // Create separate copies to avoid reference issues
      const playlistInfoBaseline = {
        title: target.playlistTitle || "",
        location: target.formLocation || LOCATION_OPTIONS[0],
        type: target.type || DEFAULT_TYPE_OPTIONS[0].label,
      };
      // Use JSON stringify/parse for complex nested objects to ensure clean copies
      const playlistAudiosBaseline = JSON.parse(JSON.stringify(normalizedPlaylistAudios));
      const thumbnailStateBaseline = JSON.parse(JSON.stringify(normalizedThumbnailState));
      // For publishState, use the same construction logic as normalizedPublishState
      const publishStateBaseline = {
        ...publishDefaults,
        ...sourcePublish,
        status: normalizePublishStatus(sourcePublish.status || target.status),
      };
      const contentSettingsBaseline = JSON.parse(JSON.stringify(normalizedContentSettings));

      // Set all baselines and editingPlaylistId together
      // They will all be available in the next render
      setPlaylistInfoBaseline(playlistInfoBaseline);
      setPlaylistAudiosBaseline(playlistAudiosBaseline);
      setThumbnailStateBaseline(thumbnailStateBaseline);
      setPublishStateBaseline(publishStateBaseline);
      setContentSettingsBaseline(contentSettingsBaseline);
      setEditingPlaylistId(playlistId);
      setIsCreateView(true);
    },
    [playlists, basePermissionState, contentSettingsConfig],
  );

  const handleSavePlaylist = async (mode = "save") => {
    // Mark that user attempted to save
    setAttemptedPlaylistSave(true);

    // Validate title
    if (!playlistInfo.title || playlistInfo.title.trim() === "") {
      setPlaylistTitleError(true);
      setApiError("Please enter a playlist title.");
      return;
    }

    setPlaylistTitleError(false);

    if (thumbnailUploading) {
      setApiError(
        "Please wait for thumbnail upload to complete before saving.",
      );
      return;
    }

    if (thumbnailState.mode === "uploaded") {
      const isBlobPreview =
        typeof thumbnailState.src === "string" &&
        thumbnailState.src.startsWith("blob:");
      if (isBlobPreview && !thumbnailTempFilename) {
        setApiError(
          "Thumbnail upload is not complete. Please re-add the image.",
        );
        return;
      }
    }

    let resolvedThumbnailState = thumbnailState;
    let resolvedThumbnailTempFilename = thumbnailTempFilename;
    let resolvedThumbnailUrl =
      thumbnailState.mode === "uploaded" ? thumbnailState.src || "" : "";

    // If using audios mode and we have audio thumbnails, build and upload the grid
    if (
      thumbnailState.mode === "audios" &&
      playlistAudios.length > 0 &&
      !resolvedThumbnailTempFilename
    ) {
      setThumbnailUploading(true);
      try {
        // Get audio thumbnails to build the grid (handles overflow with +n)
        const audioThumbnails = playlistAudios
          .map((audio) => audio.thumbnailPath)
          .filter(Boolean);

        if (audioThumbnails.length > 0) {
          // Build canvas image of the audio grid (2x2 with overflow on 4th tile)
          const gridImageBlob = await buildAudioGridImage(audioThumbnails);
          const filename = `temp_${Date.now()}_${Math.floor(
            Math.random() * 10000,
          )}.png`;
          const gridImageFile = new File([gridImageBlob], filename, {
            type: "image/png",
          });

          // Upload to temp storage
          const uploadResponse = await uploadDreamcastThumbnailFile(
            gridImageFile,
            { destination: "playlists" },
          );

          if (uploadResponse?.data?.thumbnailUrl) {
            resolvedThumbnailUrl = uploadResponse.data.thumbnailUrl;
            resolvedThumbnailTempFilename =
              uploadResponse.data.tempFilename || null;
            resolvedThumbnailState = {
              mode: "audios",
              src: resolvedThumbnailUrl,
            };

            setThumbnailState(resolvedThumbnailState);
            setThumbnailTempFilename(resolvedThumbnailTempFilename);
          }
        } else {
          // No audio thumbnails available
          setThumbnailUploading(false);
        }
      } catch (error) {
        console.error("[Playlists] Failed to upload audio thumbnail:", error);
        setApiError(
          error.message ||
            "Failed to upload audio thumbnail. Continue without it?",
        );
        setThumbnailUploading(false);
        return;
      } finally {
        setThumbnailUploading(false);
      }
    }

    const playlistType = playlistInfo.type || "Business";
    const isDraft = mode === "draft";
    const resolvedPublishStatus = publishState.status || "Published";
    const body = {
      title: playlistInfo.title || "Untitled Playlist",
      playlistType: "WWG Playlist",
      type: playlistType,
      displayStatus: getPlaylistDisplayStatus(mode, publishState.status),
      workflowStatus: getPlaylistWorkflowStatus(mode, publishState.status),
      pinPermissions:
        contentSettings.pinPermissions ||
        DEFAULT_PERMISSION_STATE.pinPermissions,
      membershipPermissions:
        contentSettings.membershipPermissions ||
        DEFAULT_PERMISSION_STATE.membershipPermissions,
      audios: playlistAudios.map((audio, index) => ({
        ...audio,
        order: index + 1,
        // Overwrite the sortOrder loaded from the API — it reflects the old
        // position and would otherwise win over the new drag order on save.
        sortOrder: index + 1,
      })),
      thumbnail: resolvedThumbnailUrl,
      thumbnailState:
        resolvedThumbnailState.mode === "audios" && resolvedThumbnailUrl
          ? {
              mode: "audios",
              customImageUrl: resolvedThumbnailUrl,
              src: resolvedThumbnailUrl,
            }
          : resolvedThumbnailState.mode === "uploaded"
          ? {
              mode: "custom",
              customImageUrl: resolvedThumbnailUrl,
              src: resolvedThumbnailUrl,
            }
          : { mode: "audios", customImageUrl: null, src: "" },
      publishState: {
        ...publishState,
        status: resolvedPublishStatus,
      },
      contentSettings: toApiContentSettings(contentSettings),
      typeGroup: playlistAudios.some((audio) => audio.typeGroup === "3rd")
        ? "3rd"
        : "wwg",
      category: playlistAudios[0]?.mediaTypeCode || "audio",
      language: playlistAudios[0]?.language || "english",
      tags: Array.from(
        new Set(
          playlistAudios.flatMap((audio) =>
            Array.isArray(audio.tags) ? audio.tags : [],
          ),
        ),
      ),
      isDraft,
    };

    try {
      setApiError("");
      setIsSaving(mode);


      let targetId = editingPlaylistId;
      if (editingPlaylistId) {
        await updateDreamcastPlaylist(editingPlaylistId, body);
      } else {
        const created = await createDreamcastPlaylist(body);
        targetId = created?.data?.playlistId || null;
      }

      if (targetId && resolvedThumbnailTempFilename) {
        setPendingImageMove({
          playlistId: targetId,
          tempFilename: resolvedThumbnailTempFilename,
          oldUrl: thumbnailPrevUrl || undefined,
        });
      }

      await loadPlaylists();

      if (pendingTypeDeletions.length > 0) {
        try {
          await Promise.all(
            pendingTypeDeletions.map((type) =>
              deleteDreamcastPlaylistType(type),
            ),
          );
          await loadPlaylistTypes();
        } catch (error) {
          setApiError(error.message || "Failed to delete playlist type.");
        }
      }

      if (targetId) {
        try {
          await fetchDreamcastPlaylistById(targetId);
        } catch {
          // detail fetch is best-effort
        }
      }

      setIsCreateView(false);
      resetCreateView();
    } catch (error) {
      setApiError(error.message || "Failed to save playlist.");
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (!pendingImageMove) {
      return;
    }

    const processPendingImageMove = async () => {
      try {
        await moveDreamcastPlaylistTempImage(
          pendingImageMove.playlistId,
          pendingImageMove.tempFilename,
          pendingImageMove.oldUrl,
        );
        await loadPlaylists();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("[Playlists] Failed to move thumbnail image:", error);
        setApiError(
          error.message ||
            "Thumbnail moved to temporary location. Please refresh and retry.",
        );
      } finally {
        setPendingImageMove(null);
      }
    };

    processPendingImageMove();
  }, [pendingImageMove, loadPlaylists]);

  const handleDuplicatePlaylist = async (playlistId) => {
    try {
      setApiError("");
      const sourcePlaylist = playlists.find((p) => p.playlistId === playlistId);
      if (!sourcePlaylist) return;

      // Track the ORIGINAL playlist ID as duplicating
      setDuplicatingPlaylistId(playlistId);

      // Duplicate in the background
      await duplicateDreamcastPlaylist(playlistId);

      // Update filters to show upcoming items if they're not already shown
      setPlaylistFilters((current) => {
        const currentStatus = Array.isArray(current.Status)
          ? current.Status
          : [];

        // Duplicates are created as upcoming/in-review drafts.
        if (
          currentStatus.length > 0 &&
          !currentStatus.includes("upcoming") &&
          !currentStatus.includes("in-review")
        ) {
          return {
            ...current,
            Status: [...currentStatus, "upcoming", "in-review"],
          };
        }

        return current;
      });

      // Reload to sync with server
      await loadPlaylists();
    } catch (error) {
      setApiError(error.message || "Failed to duplicate playlist.");
      await loadPlaylists();
    } finally {
      // Clear duplicate tracking after a brief delay to ensure UI updates
      setTimeout(() => {
        setDuplicatingPlaylistId(null);
      }, 500);
    }
  };

  const handleArchivePlaylist = async (playlistId) => {
    const target = playlists.find((p) => p.playlistId === playlistId);
    if (!target) return;

    const isArchived = target.workflowStatus === "archived";
    const workflowStatus = isArchived ? "published" : "archived";
    const displayStatus = isArchived ? "Available" : "Archived";

    setArchivingPlaylistId(playlistId);
    try {
      setApiError("");
      await setDreamcastPlaylistStatus(
        playlistId,
        workflowStatus,
        displayStatus,
      );
      await loadPlaylists();
    } catch (error) {
      setApiError(error.message || "Failed to update playlist status.");
    } finally {
      setTimeout(() => {
        setArchivingPlaylistId(null);
      }, 500);
    }
  };

  const handleOpenAudioSelection = async () => {
    let currentAudioLibrary = audioLibrary;

    // Show loading state immediately
    setIsLoadingList(true);
    setIsSelectingAudios(true);

    try {
      setApiError("");
      currentAudioLibrary = await loadAudioLibrary();
    } catch (error) {
      setApiError(error.message || "Failed to load audio library.");
      setIsSelectingAudios(false);
      setIsLoadingList(false);
      return;
    }

    setSelectedAudioIds(
      currentAudioLibrary
        .filter((libraryAudio) =>
          playlistAudios.some((playlistAudio) =>
            playlistAudioMatchesLibraryAudio(playlistAudio, libraryAudio),
          ),
        )
        .map((audio) => toAudioSelectionId(audio.mediaItemId)),
    );

    // Loading complete, hide loader
    setIsLoadingList(false);
  };

  const handleAddSelectedAudios = () => {
    const selectedLookup = new Set(selectedAudioIds);

    // Keep existing audios that are still selected (preserves drag order)
    const kept = playlistAudios.filter((audio) =>
      selectedLookup.has(toAudioSelectionId(audio.mediaItemId)),
    );
    // Append newly added audios (not already in the playlist) in library order
    const added = audioLibrary
      .filter(
        (audio) =>
          selectedLookup.has(toAudioSelectionId(audio.mediaItemId)) &&
          !kept.some((playlistAudio) =>
            playlistAudioMatchesLibraryAudio(playlistAudio, audio),
          ),
      )
      .map((audio) => ({ ...audio }));

    const nextSelection = [...kept, ...added].map((audio, index) => ({
      ...audio,
      order: index + 1,
    }));

    setPlaylistAudios(nextSelection);
    setIsSelectingAudios(false);
  };

  const handleSelectAudio = (audioId) => {
    const selectionId = toAudioSelectionId(audioId);

    setSelectedAudioIds((current) => {
      if (current.includes(selectionId)) {
        return current.filter((id) => id !== selectionId);
      }
      return [...current, selectionId];
    });
  };

  const handleSelectAllDisplayed = () => {
    setSelectedAudioIds((current) => {
      const next = new Set(current);
      selectableAudios.forEach((audio) =>
        next.add(toAudioSelectionId(audio.mediaItemId)),
      );
      return Array.from(next);
    });
  };

  const handleUnselectAll = () => {
    setSelectedAudioIds([]);
  };

  const renderSelectionMode = () => {
    if (isLoadingList) {
      return (
        <div className={styles.initialLoaderOverlay}>
          <Loader color="#415364" size={85} />
        </div>
      );
    }

    return (
      <section className={styles.selectionLayout}>
        <div className={styles.selectionToolbar}>
          <SearchBar
            placeholderText="Search Media"
            value={selectionSearchQuery}
            onInput={setSelectionSearchQuery}
            noResults={selectionSearchBarNoResults}
            minChar={0}
            searchDelay={0}
          />
          <SortModal
            options={SORT_OPTIONS}
            selected={selectionSortBy}
            onChange={setSelectionSortBy}
          />
          <div className={styles.selectionActions}>
            <span className={styles.selectedCount}>
              Selected Media: {selectedAudioIds.length}
            </span>
            <Button
              className={styles.addSelectedButton}
              onClick={handleAddSelectedAudios}
            >
              +Add
            </Button>
            <Button
              className={styles.cancelSelectButton}
              onClick={() => setIsSelectingAudios(false)}
            >
              Cancel
            </Button>
            <Button
              className={styles.selectAllButton}
              onClick={handleSelectAllDisplayed}
            >
              Select All
            </Button>
            <Button
              className={styles.unselectAllButton}
              onClick={handleUnselectAll}
            >
              Unselect All
            </Button>
          </div>
        </div>

        <div className={styles.selectionBody}>
          <FilterColumn
            className={styles.filterColumn}
            sections={audioFilterSections}
            selected={selectionFilters}
            onChange={setSelectionFilters}
          />

          <div className={styles.selectionListContent}>
            <div className={styles.columnHeaders}>
              <span className={styles.colImage}>Image</span>
              <div className={styles.headerInfoCluster}>
                <span className={styles.colInfo}>Title/Info</span>
                <div className={styles.headerRightColumn}>
                  <span className={styles.colSku}>SKU</span>
                  <span className={styles.colType}>Type</span>
                  <span className={styles.colStatus}>Status</span>
                </div>
              </div>
            </div>

            {selectionFiltersNoResults ? (
              <p className={styles.emptyState}>No results found.</p>
            ) : (
              <div className={styles.listItems}>
                {selectableAudios.map((item) => (
                  <AudioListItem
                    key={item.mediaItemId}
                    id={item.mediaItemId}
                    thumbnail={item.thumbnailPath}
                    title={item.title}
                    date={item.date}
                    speaker={item.contributorName}
                    episode={item.episodePart}
                    sku={item.sku}
                    type={item.mediaTypeCode}
                    status={item.displayStatus}
                    showMembershipPermissions={false}
                    tags={item.tags || []}
                    selectable
                    selected={selectedAudioIds.includes(
                      toAudioSelectionId(item.mediaItemId),
                    )}
                    onSelect={handleSelectAudio}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    );
  };

  const renderCreateMode = () => {
    if (isSelectingAudios) {
      return renderSelectionMode();
    }

    // Determine if form has changes
    // In edit mode, compare against baseline; in create mode, check if anything is filled
    const isEditMode =
      editingPlaylistId && playlistInfoBaseline && Array.isArray(playlistAudiosBaseline) && isEditingBaselinesReady;
    const isPlaylistDirty = isEditMode
        ? // Edit mode: simple baseline comparison
          !!(
            playlistInfo.title !== playlistInfoBaseline.title ||
            playlistInfo.location !== playlistInfoBaseline.location ||
            playlistInfo.type !== playlistInfoBaseline.type ||
            JSON.stringify(playlistAudios) !== JSON.stringify(playlistAudiosBaseline) ||
            JSON.stringify(thumbnailState) !== JSON.stringify(thumbnailStateBaseline) ||
            JSON.stringify(publishState) !== JSON.stringify(publishStateBaseline) ||
            JSON.stringify(contentSettings) !== JSON.stringify(contentSettingsBaseline)
          )
        : editingPlaylistId && !isEditingBaselinesReady
          ? // Edit mode but baselines not ready yet - keep buttons disabled
            false
          : // Create mode: compare against baseline set during reset
            playlistInfo.title.trim() !== "" ||
            playlistInfo.type !== "Business" ||
            playlistInfo.location !== LOCATION_OPTIONS[0] ||
            playlistAudios.length > 0 ||
            thumbnailState.mode !== "none" ||
            (publishState.status && publishState.status !== "Published") ||
            JSON.stringify(contentSettings) !== JSON.stringify(contentSettingsBaseline);

    // Disable save if there's an error or validation failed
    const shouldDisableSave = playlistTitleError || !!apiError;
    const buttonDisabled = isSaving || shouldDisableSave || !isPlaylistDirty;

    return (
      <section className={styles.createLayout}>
        <div className={styles.actionBarRow}>
          <FormActionBar
            onSave={() => handleSavePlaylist("save")}
            onSaveDraft={() => handleSavePlaylist("draft")}
            onCancel={() => {
              setIsCreateView(false);
              resetCreateView();
            }}
            isDraft={editingWasDraft}
            isSaving={isSaving}
            disabled={buttonDisabled}
            isDirty={isPlaylistDirty}
            embedded
          />
        </div>

        <div className={styles.formColumns}>
          <div className={styles.leftColumn}>
            <PlaylistInfoForm
              title={playlistInfo.title}
              location={playlistInfo.location}
              locationOptions={LOCATION_OPTIONS}
              type={playlistInfo.type}
              typeLabel="Theme"
              typeOptions={typeOptions.filter(
                (option) => !pendingTypeDeletions.includes(option.label),
              )}
              titleError={attemptedPlaylistSave && playlistTitleError}
              onChange={handlePlaylistInfoChange}
              onAddType={async (label) => {
                const nextLabel = label.trim();
                if (!nextLabel) return;

                try {
                  await createDreamcastPlaylistType(nextLabel);
                  await loadPlaylistTypes();
                } catch (error) {
                  setApiError(error.message || "Failed to add playlist type.");
                  return;
                }

                setPlaylistInfo((current) => ({ ...current, type: nextLabel }));
              }}
              onDeleteType={(label) => {
                setPendingTypeDeletions((current) => [...current, label]);

                setPlaylistInfo((current) => ({
                  ...current,
                  type:
                    current.type === label
                      ? DEFAULT_TYPE_OPTIONS[0].label
                      : current.type,
                }));
              }}
            />

            <PlaylistThumbnailPreview
              thumbnails={coverArtData.thumbnails}
              overflowCount={coverArtData.overflowCount}
              hasAudios={coverArtData.hasPlaylistAudios}
              onAddExisting={async (file) => {
                if (!file) {
                  return;
                }

                const imageUrl = URL.createObjectURL(file);
                setThumbnailState({ mode: "uploaded", src: imageUrl });
                setThumbnailTempFilename(null);
                setThumbnailUploading(true);

                try {
                  const response = await uploadDreamcastThumbnailFile(file, {
                    destination: "playlists",
                  });

                  if (response?.data?.thumbnailUrl) {
                    setThumbnailState({
                      mode: "uploaded",
                      src: response.data.thumbnailUrl,
                    });
                    setThumbnailTempFilename(
                      response?.data?.tempFilename || null,
                    );
                  }
                } catch {
                  // Keep local preview URL when upload fails.
                } finally {
                  setThumbnailUploading(false);
                }
              }}
              onDrop={async (file) => {
                if (!file) {
                  return;
                }

                const imageUrl = URL.createObjectURL(file);
                setThumbnailState({ mode: "uploaded", src: imageUrl });
                setThumbnailTempFilename(null);
                setThumbnailUploading(true);

                try {
                  const response = await uploadDreamcastThumbnailFile(file, {
                    destination: "playlists",
                  });

                  if (response?.data?.thumbnailUrl) {
                    setThumbnailState({
                      mode: "uploaded",
                      src: response.data.thumbnailUrl,
                    });
                    setThumbnailTempFilename(
                      response?.data?.tempFilename || null,
                    );
                  }
                } catch {
                  // Keep local preview URL when upload fails.
                } finally {
                  setThumbnailUploading(false);
                }
              }}
              onUseAudios={() => {
                setThumbnailState({ mode: "audios", src: "" });
                setThumbnailTempFilename(null);
              }}
              onEdit={() => {
                setThumbnailState({ mode: "none", src: "" });
                setThumbnailTempFilename(null);
              }}
              onDelete={() => {
                setThumbnailState({ mode: "none", src: "" });
                setThumbnailTempFilename(null);
              }}
            />

            <PlaylistAudiosSection
              audios={playlistAudios}
              searchValue={playlistAudioSearchQuery}
              onSearch={setPlaylistAudioSearchQuery}
              onAdd={handleOpenAudioSelection}
              isLoadingAudioSelection={isLoadingList}
              onRemove={(audioId) => {
                setPlaylistAudios((current) =>
                  current.filter((item) => item.mediaItemId !== audioId),
                );
              }}
              onReorder={setPlaylistAudios}
            />
          </div>

          <div className={styles.rightColumn}>
            <PublishCard
              status={publishState.status}
              publishOn={publishState.publishOn}
              unpublishOn={publishState.unpublishOn}
              showAsCurrentUntil={publishState.showAsCurrentUntil}
              unpublishEnabled={publishState.unpublishEnabled}
              digitalDeliveryEnabled={publishState.digitalDeliveryEnabled}
              showAsUpcomingOn={publishState.showAsUpcomingOn}
              showDigitalDelivery={false}
              onChange={(nextPartial) =>
                setPublishState((current) => ({ ...current, ...nextPartial }))
              }
            />

            <ContentSettingsCard
              membershipPermissions={contentSettings.membershipPermissions}
              pinPermissions={contentSettings.pinPermissions}
              membershipGroupValuesById={
                contentSettings.membershipGroupValuesById
              }
              membershipOptions={contentSettings.membershipOptions}
              pinLevels={contentSettings.pinLevels}
              familyReunionGroups={contentSettings.familyReunionGroups}
              selectedFamilyGroups={contentSettings.selectedFamilyGroups}
              diamonds={contentSettings.diamonds}
              selectedDiamonds={contentSettings.selectedDiamonds}
              additionalVisibilitySections={
                contentSettings.additionalVisibilitySections
              }
              visibilitySelectionsByTypeCode={
                contentSettings.visibilitySelectionsByTypeCode
              }
              familyReunionSectionTitle={
                contentSettings.familyReunionSectionTitle
              }
              diamondsSectionTitle={contentSettings.diamondsSectionTitle}
              additionalIbos={contentSettings.additionalIbos}
              tags={contentSettings.tags}
              onChange={setContentSettings}
              onTagCreated={handleTagCreated}
            />
          </div>
        </div>
      </section>
    );
  };

  const renderListMode = () => {
      if (isLoadingList && playlists.length === 0) {
      return (
        <div className={styles.initialLoaderOverlay}>
          <Loader color="#415364" size={85} />
        </div>
      );
    }

    return (
      <section className={styles.listLayout}>
        <div className={styles.toolbar}>
          <SearchBar
            placeholderText="Search Playlists"
            value={playlistSearchQuery}
            onInput={setPlaylistSearchQuery}
            noResults={playlistSearchBarNoResults}
            minChar={0}
            searchDelay={0}
          />
          <Button
            className={styles.addPlaylistButton}
            onClick={handleOpenCreateView}
          >
            Add New Playlist
          </Button>
          <SortModal
            options={PLAYLIST_SORT_OPTIONS}
            selected={playlistSortBy}
            onChange={setPlaylistSortBy}
          />
        </div>

        <div className={styles.listBody}>
          <FilterColumn
            className={styles.filterColumn}
            sections={playlistFilterSections}
            selected={playlistFilters}
            onChange={setPlaylistFilters}
          />

          <div className={styles.listContent}>
            <div className={styles.playlistColumnHeaders}>
              <span className={styles.colImage}>Image</span>
              <div className={styles.playlistHeaderInfoCluster}>
                <span className={styles.colInfo}>Title/Info</span>
                <div className={styles.playlistHeaderRightColumn}>
                  <span className={styles.colLocation}>Location</span>
                  <span className={styles.colType}>Theme</span>
                  <span className={styles.colStatus}>Status</span>
                </div>
              </div>
            </div>

            {playlistFiltersNoResults ? (
              <p className={styles.emptyState}>No results found.</p>
            ) : (
              <div className={styles.playlistRows}>
                {filteredPlaylists.map((playlist) => {
                  const cover = buildCoverArtData(
                    normalizeThumbnailStateFromApi(
                      playlist.thumbnailState,
                      playlist.thumbnailPath,
                    ),
                    playlist.playlistMedia || [],
                  );

                  const isEditLoading =
                    String(editingPlaylistId) === String(playlist.playlistId);
                  const isDuplicateLoading =
                    String(duplicatingPlaylistId) ===
                    String(playlist.playlistId);
                  const isArchiveLoading =
                    String(archivingPlaylistId) === String(playlist.playlistId);

                  return (
                    <PlaylistListItem
                      key={playlist.playlistId}
                      id={playlist.playlistId}
                      thumbnails={cover.thumbnails}
                      overflowCount={cover.overflowCount}
                      title={playlist.playlistTitle}
                      publishDate={playlist.date}
                      playlistType={playlist.playlistType || "WWG Playlist"}
                      location={playlist.location || "WWG"}
                      type={playlist.type}
                      status={
                        playlist.displayStatus || playlist.status || "Available"
                      }
                      isDraft={Boolean(playlist.isDraft)}
                      pinPermissions={
                        playlist.pinPermissions ||
                        DEFAULT_PERMISSION_STATE.pinPermissions
                      }
                      membershipPermissions={
                        playlist.membershipPermissions ||
                        DEFAULT_PERMISSION_STATE.membershipPermissions
                      }
                      tags={playlist.tags || []}
                      hiddenTags={Array.isArray(playlist.contentSettings?.tags?.hiddenTags) ? playlist.contentSettings.tags.hiddenTags : []}
                      onDuplicate={handleDuplicatePlaylist}
                      onArchive={handleArchivePlaylist}
                      onEdit={handleEditPlaylist}
                      isEditLoading={isEditLoading}
                      isDuplicateLoading={isDuplicateLoading}
                      isArchiveLoading={isArchiveLoading}
                    />
                  );
                })}
              </div>
            )}

          </div>
        </div>
      </section>
    );
  };

  const handlePlaylistInfoChange = (newInfo) => {
    setPlaylistInfo(newInfo);
    // Clear title error when user modifies the title
    if (newInfo.title && newInfo.title.trim() !== "") {
      setPlaylistTitleError(false);
    }
  };

  return (
    <div className={styles.page}>
      <ControlsHeader title="DreamCast - Playlists" />
      {apiError && <p className={styles.apiError}>{apiError}</p>}
      {isCreateView ? renderCreateMode() : renderListMode()}
    </div>
  );
}

export default Playlists;
